import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost, ApiError } from "../api";
import type { ID } from "../id";
import {
  hashPairingCode,
  newPairingCode,
  pairingQrText,
} from "@shared/live/pairingCode";
import type { Station } from "@shared/live/protocol";
import type { DeviceFunction } from "@shared/live/labels";

export type { Station } from "@shared/live/protocol";
export type { DeviceFunction } from "@shared/live/labels";
export { DEVICE_FUNCTION_LABELS, stationLabel } from "@shared/live/labels";

/**
 * Hall devices of a competition (docs/zivy-provoz-soutezi.md §7, §7b): who
 * is paired to which station or function, and codes waiting to be
 * redeemed. A judge's tablet stands for a place at a judging table
 * (stůl), never for a panel: panels take turns at a table and the tablets
 * stay. Swapping a dead tablet is pairing a spare to its station. While the
 * competition runs the server turns every change into a live command.
 */

/** A judging table and the places it has (from the start list). */
export interface JudgingTable {
  table: number;
  plus_judges: number;
  tech_judges: number;
}

export interface Device {
  id: ID;
  station?: Station;
  function?: DeviceFunction;
  label: string;
  app_version?: string;
  paired_at_ms: number;
  revoked_at_ms?: number;
}

export interface Pairing {
  id: ID;
  station?: Station;
  function?: DeviceFunction;
  label: string;
  expires_at_ms: number;
}

export interface DeviceList {
  tables: JudgingTable[];
  devices: Device[];
  pairings: Pairing[];
}

const listKey = (competitionId: ID) => [
  "competitions",
  "devices",
  competitionId,
];

export function useDevices(competitionId: ID | undefined) {
  return useQuery<DeviceList>({
    queryKey: listKey(competitionId ?? ""),
    enabled: !!competitionId,
    staleTime: 0,
    // A tablet redeems its code in the hall while this screen is open.
    refetchInterval: 5_000,
    queryFn: () => apiGet<DeviceList>(`/competitions/${competitionId}/devices`),
  });
}

export interface NewPairing {
  station?: Station;
  function?: DeviceFunction;
  label: string;
}

/**
 * Makes a pairing code. The code is generated HERE and the server gets
 * only its hash, so it never sees the code; the caller shows it.
 */
export function useCreatePairing(competitionId: ID) {
  const qc = useQueryClient();
  return useMutation<
    { id: ID; code: string; expires_at_ms: number },
    ApiError,
    NewPairing
  >({
    mutationFn: async (p) => {
      const code = newPairingCode();
      const res = await apiPost<{ id: ID; expires_at_ms: number }>(
        `/competitions/${competitionId}/devices/pairings`,
        { ...p, code_hash: await hashPairingCode(code) },
      );
      return { id: res.id, code, expires_at_ms: res.expires_at_ms };
    },
    // The dialog shows the error.
    meta: { suppressErrorToast: true },
    onSuccess: () => qc.invalidateQueries({ queryKey: listKey(competitionId) }),
  });
}

export function useRevokeDevice(competitionId: ID) {
  const qc = useQueryClient();
  return useMutation<unknown, ApiError, ID>({
    mutationFn: (id) =>
      apiDelete(`/competitions/${competitionId}/devices/${id}`),
    // The confirmation dialog shows the error.
    meta: { suppressErrorToast: true },
    onSuccess: () => qc.invalidateQueries({ queryKey: listKey(competitionId) }),
  });
}

/**
 * What a pairing QR says: the server to pair with and the code in its wire
 * form, exactly what the app then sends to `POST /api/live/pair`.
 */
export function pairingQrPayload(code: string): string {
  return pairingQrText(window.location.origin, code);
}
